import {useState, useCallback} from 'react';

import {ContentRequester} from '../../Domain/Groups/ContentRequester';

const PAGE_SIZE = 20;
const FETCH_ALL = 10000;

let projectsCache = null;

export const clearProjectsCache = () => { projectsCache = null; };

const useContentTree = (workspace, {includePublishedRoles = false, fetchAllProjects = false} = {}) => {
    const [projects, setProjects] = useState([]);
    const [totalProjects, setTotalProjects] = useState(0);
    const [childrenCache, setChildrenCache] = useState({});
    const [expandedNodes, setExpandedNodes] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [loadingNodes, setLoadingNodes] = useState(new Set());
    const [visibleCounts, setVisibleCounts] = useState({});

    const loadProjects = useCallback(async (skip = 0, search = '') => {
        if (skip === 0 && !search && fetchAllProjects && projectsCache?.workspace === workspace) {
            setProjects(projectsCache.data);
            setTotalProjects(projectsCache.totalCount);
            return;
        }
        setIsLoading(true);
        try {
            const result = await ContentRequester.getProjects(workspace, {skip, limit: fetchAllProjects ? FETCH_ALL : PAGE_SIZE, search});
            setProjects((prev) => (skip === 0 ? result.data : [...prev, ...result.data]));
            setTotalProjects(result.totalCount);
            if (skip === 0 && !search && fetchAllProjects) {
                projectsCache = {workspace, data: result.data, totalCount: result.totalCount};
            }
        } catch (error) {
            console.error('Failed to load projects:', error);
        } finally {
            setIsLoading(false);
        }
    }, [workspace, fetchAllProjects]);

    const loadChildren = useCallback(async (nodeId, nodeType, nestedRoute) => {
        setLoadingNodes((prev) => new Set(prev).add(nodeId));
        try {
            const result = await ContentRequester.getChildrenFoldersAndProcedures(workspace, {
                parentId: nodeId,
                parentType: nodeType,
                nestedRoute,
                skip: 0,
                limit: FETCH_ALL,
                includePublishedRoles,
            });
            setChildrenCache((prev) => ({
                ...prev,
                [nodeId]: {
                    folders: result.folders,
                    items: result.items,
                    totalFolders: result.totalFolders,
                    totalItems: result.totalItems,
                },
            }));
        } catch (error) {
            console.error('Failed to load children:', error);
        }
        setLoadingNodes((prev) => {
            const next = new Set(prev);
            next.delete(nodeId);
            return next;
        });
    }, [workspace, includePublishedRoles]);

    const handleExpand = useCallback(async (nodeId, nodeType, nestedRoute) => {
        const next = new Set(expandedNodes);
        if (next.has(nodeId)) {
            next.delete(nodeId);
        } else {
            next.add(nodeId);
            if (!childrenCache[nodeId]) {
                await loadChildren(nodeId, nodeType, nestedRoute);
            }
        }
        setExpandedNodes(next);
    }, [expandedNodes, childrenCache, loadChildren]);

    const handleLoadMoreChildren = useCallback((nodeId) => {
        setVisibleCounts((prev) => ({...prev, [nodeId]: (prev[nodeId] || PAGE_SIZE) + PAGE_SIZE}));
    }, []);

    const handleLoadMoreProjects = useCallback(async () => {
        await loadProjects(projects.length, searchQuery);
    }, [projects.length, searchQuery, loadProjects]);

    const handleSearch = useCallback(async (query) => {
        setSearchQuery(query);
        setExpandedNodes(new Set());
        setChildrenCache({});
        setVisibleCounts({});
        await loadProjects(0, query);
    }, [loadProjects]);

    const reset = useCallback(async () => {
        setProjects([]);
        setTotalProjects(0);
        setChildrenCache({});
        setExpandedNodes(new Set());
        setSearchQuery('');
        setLoadingNodes(new Set());
        setVisibleCounts({});
        await loadProjects(0, '');
    }, [loadProjects]);

    const getSnapshot = useCallback(() => ({
        projects, totalProjects, childrenCache, expandedNodes: [...expandedNodes],
    }), [projects, totalProjects, childrenCache, expandedNodes]);

    const restoreSnapshot = useCallback((snapshot) => {
        setProjects(snapshot.projects);
        setTotalProjects(snapshot.totalProjects);
        setChildrenCache(snapshot.childrenCache);
        setExpandedNodes(new Set(snapshot.expandedNodes));
        setSearchQuery('');
        setLoadingNodes(new Set());
        setVisibleCounts({});
    }, []);

    const hasMoreProjects = projects.length < totalProjects;

    const hasMoreChildren = useCallback((nodeId) => {
        const cached = childrenCache[nodeId];
        if (!cached) return false;
        const total = (cached.folders?.length || 0) + (cached.items?.length || 0);
        const visible = visibleCounts[nodeId] || PAGE_SIZE;
        return visible < total;
    }, [childrenCache, visibleCounts]);

    return {
        projects,
        totalProjects,
        childrenCache,
        expandedNodes,
        searchQuery,
        isLoading,
        loadingNodes,
        hasMoreProjects,
        hasMoreChildren,
        visibleCounts,
        reset,
        getSnapshot,
        restoreSnapshot,
        handleExpand,
        handleLoadMoreChildren,
        handleLoadMoreProjects,
        handleSearch,
    };
};

export default useContentTree;
